import { useState, useCallback, useEffect } from 'react';
import { useSupabaseAuth } from '@/hooks/auth';
import { LearningStyle } from '@/ml/types';
import { supabase } from '@/integrations/supabase/client';

// Simple UUID generator
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

interface CodeExample {
  title: string;
  code: string;
  language: string;
  explanation: string;
}

interface PracticeExercise {
  id: string;
  title: string;
  description: string;
  courseId: string;
  moduleId: string;
}

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  difficulty: string;
}

interface RelatedCourse {
  courseId: string;
  courseTitle: string;
  moduleId: string;
  moduleTitle: string;
  difficulty: string;
  category: string;
}

interface ChatResponse {
  overview: string;
  content: string;
  detailedExplanation?: string;
  keyPoints?: string[];
  steps?: { step: number; description: string }[];
  codeExamples?: CodeExample[];
  practiceExercises?: PracticeExercise[];
  quizQuestions?: QuizQuestion[];
  relatedTopics?: string[];
  relatedCourses?: RelatedCourse[];
  followUpSuggestions?: string[];
  courseTitle?: string;
  moduleTitle?: string;
  courseId?: string;
  moduleId?: string;
  difficulty?: string;
  category?: string;
}
interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  metadata?: ChatResponse;
}

interface MLChatbotState {
  messages: ChatMessage[];
  isLoading: boolean;
  error?: string;
  sessionId: string;
}

export const useMLChatbot = () => {
  const { user } = useSupabaseAuth();
  const [state, setState] = useState<MLChatbotState>({
    messages: [],
    isLoading: false,
    sessionId: generateUUID()
  });

  // Mock learning style data
  const mockLearningStyle: LearningStyle = {
    visual: 0.5,
    textual: 0.7,
    interactive: 0.4,
    auditory: 0.3,
    practicalCoding: 0.6
  };

  // Conversation history is managed by the edge function
  // Messages persist in database automatically

  const sendMessage = useCallback(async (content: string, mode: 'chat' | 'debug' | 'recommend' = 'chat') => {
    if (!user) return;

    console.log('Sending message:', content);

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date().toISOString()
    };

    setState(prev => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      isLoading: true,
      error: undefined
    }));

    try {
      console.log('🔐 Calling intelligent-mentor-chat edge function');

      // Create empty assistant message for streaming
      const assistantId = (Date.now() + 1).toString();
      const assistantMessage: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
      };

      setState(prev => ({
        ...prev,
        messages: [...prev.messages, assistantMessage],
      }));

      // Call edge function using Supabase client
      const { data, error } = await supabase.functions.invoke('intelligent-mentor-chat', {
        body: {
          message: content,
          mode,
          conversationHistory: state.messages.map(m => ({
            role: m.role,
            content: m.content
          })),
          userId: user.id,
          sessionId: state.sessionId
        }
      });

      if (error) {
        console.error('❌ Edge function error:', error);
        throw new Error(error.message || 'Failed to get response from AI mentor');
      }

      if (!data) {
        console.error('❌ No data returned from edge function');
        throw new Error('No response received from AI mentor');
      }

      const fullContent = data.content || '';

      console.log('✅ Response received:', fullContent.substring(0, 100));

      // Simulate streaming for better UX (show text progressively)
      const words = fullContent.split(' ');
      let currentContent = '';

      for (let i = 0; i < words.length; i++) {
        currentContent += (i > 0 ? ' ' : '') + words[i];
        
        setState(prev => ({
          ...prev,
          messages: prev.messages.map(msg => 
            msg.id === assistantId 
              ? { ...msg, content: currentContent }
              : msg
          )
        }));
        
        // Small delay for progressive display effect (adjust for speed)
        if (i % 3 === 0) { // Update every 3 words to reduce re-renders
          await new Promise(resolve => setTimeout(resolve, 20));
        }
      }

      // Final update to ensure complete content
      setState(prev => ({
        ...prev,
        messages: prev.messages.map(msg => 
          msg.id === assistantId 
            ? { ...msg, content: fullContent }
            : msg
        )
      }));

      setState(prev => ({ ...prev, isLoading: false }));
    } catch (error) {
      console.error('Error generating response:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to get response from AI mentor. Please try again.',
        isLoading: false
      }));
    }
  }, [user, state.messages, state.sessionId]);

  const clearMessages = useCallback(() => {
    setState({
      messages: [],
      isLoading: false,
      sessionId: generateUUID()
    });
  }, []);

  return {
    messages: state.messages,
    isLoading: state.isLoading,
    error: state.error || '',
    sendMessage,
    clearMessages,
    clearChat: clearMessages, // Alias for compatibility
    learningStyle: mockLearningStyle, // Return proper LearningStyle object
    isMLInitialized: true, // Mock data
    hasInitializationTimedOut: false, // Mock data
    isInitializing: false, // Mock data
    supportedTopics: [
      // Programming Languages
      'Python', 'JavaScript', 'Java', 'C#', 'TypeScript', 'SQL', 'React', 'Node.js',
      
      // AI & Machine Learning
      'Machine Learning', 'Deep Learning', 'Neural Networks', 'Natural Language Processing',
      'Computer Vision', 'TensorFlow', 'PyTorch', 'AI Ethics', 'Large Language Models', 'MLOps',
      
      // Cloud Platforms
      'AWS', 'Microsoft Azure', 'Google Cloud Platform', 'Docker', 'Kubernetes', 
      'Serverless Computing', 'Cloud Architecture', 'DevOps', 'CI/CD',
      
      // Data Science & Analytics
      'Data Science', 'Data Analysis', 'Business Intelligence', 'Power BI', 'Tableau',
      'Big Data', 'Data Engineering', 'Apache Spark', 'Data Warehousing',
      
      // Security & DevOps
      'Cybersecurity', 'Network Security', 'Web Security', 'Penetration Testing',
      'DevOps Practices', 'Infrastructure as Code', 'Security Auditing',
      
      // RPA & Automation
      'UiPath', 'Power Automate', 'Process Automation', 'Bot Development',
      'Workflow Design', 'Business Process Analysis', 'Automation Best Practices',
      
      // Mobile & Frontend Development
      'React Native', 'Flutter', 'iOS Development', 'Android Development',
      'Progressive Web Apps', 'Mobile UI/UX', 'Cross-platform Development',
      
      // Emerging Technologies
      'Blockchain', 'Web3', 'Smart Contracts', 'Cryptocurrency', 'IoT Development',
      'Edge Computing', 'AR/VR Development', 'Quantum Computing', 'Metaverse',
      
      // Professional Development
      'Project Management', 'Agile Methodologies', 'Leadership', 'Communication Skills',
      'Technical Writing', 'Software Architecture', 'System Design',
      
      // Testing & Quality Assurance
      'Software Testing', 'Test Automation', 'Selenium', 'Unit Testing',
      'Integration Testing', 'Performance Testing', 'Quality Assurance'
    ], // Comprehensive topic coverage
    sessionId: state.sessionId
  };
};
